import { normalizePracticeMatch } from "./practiceAlgebra";
import { getPracticeGeneratorMeta } from "./practiceGeneratorCatalog";
import {
  assertInstanceVerified,
  mintPracticeInstance,
  type PracticeInstance,
} from "./practiceGenerators";
import type { PracticeLocale } from "./practiceLocale";
import type { PracticeProblemPart } from "./practiceParts";

/** Public drill placed on the board. The answer stays on the server. */
export type VerifiedDrillExercise = {
  prompt: string;
  instanceId: string;
};

const INSTANCE_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isPracticeInstanceId(value: string): boolean {
  return INSTANCE_UUID_RE.test(value.trim());
}

export function drillInstanceId(
  parts: PracticeProblemPart[] | undefined,
  activePartId?: string,
): string | undefined {
  if (!parts?.length) return undefined;
  const wanted = (activePartId ?? "").trim();
  if (!wanted) return undefined;
  const part = parts.find((row) => row.id === wanted);
  const raw = part?.instanceId?.trim() ?? "";
  return isPracticeInstanceId(raw) ? raw : undefined;
}

export function onlyCodeCheckedDrillsTip(locale: PracticeLocale): string {
  return locale === "he"
    ? "אני מוסיף ללוח רק תרגילים שהתשובה שלהם נבדקת בקוד. בקשו תרגיל חשבון, אחוז, או משוואה בנעלם אחד."
    : "I only add drills whose answers can be checked in code. Ask for arithmetic, a percent, or a one-step equation.";
}

export function toVerifiedExercise(
  instance: Pick<PracticeInstance, "id" | "prompt">,
): VerifiedDrillExercise {
  return { prompt: instance.prompt, instanceId: instance.id };
}

function compactPrompt(prompt: string): string {
  return prompt
    .replace(/−/g, "-")
    .replace(/–/g, "-")
    .replace(/×/g, "*")
    .replace(/÷/g, "/")
    .replace(/\s+/g, "");
}

function claimedIs(claimed: string, expected: string, also: string[] = []): boolean {
  const got = normalizePracticeMatch(claimed);
  return [expected, ...also].map(normalizePracticeMatch).includes(got);
}

function parseCoeff(raw: string): number | null {
  if (raw === "" || raw === "+") return 1;
  if (raw === "-") return -1;
  const n = Number(raw);
  if (!Number.isInteger(n) || n === 0) return null;
  return n;
}

function verifyLinear(
  compact: string,
  claimed: string,
): { expectedAnswer: string; acceptedAnswers: string[] } | null {
  const matches = [
    ...compact.matchAll(/(-?\d*)x(?:([+-])(\d+))?=(-?\d+)/gi),
  ];
  if (matches.length !== 1) return null;
  const match = matches[0];
  const coeff = parseCoeff(match[1] ?? "");
  const rhs = Number(match[4]);
  if (coeff == null || !Number.isInteger(rhs)) return null;
  const b = match[2]
    ? match[2] === "-"
      ? -Number(match[3])
      : Number(match[3])
    : 0;
  if (!Number.isInteger(b)) return null;
  const numer = rhs - b;
  if (numer % coeff !== 0) return null;
  const x = numer / coeff;
  const expected = `x=${x}`;
  if (new RegExp(`x=${x}(?!\\d)`).test(compact)) return null;
  if (!claimedIs(claimed, expected, [String(x)])) return null;
  return { expectedAnswer: expected, acceptedAnswers: [String(x)] };
}

function verifyPercent(
  compact: string,
  claimed: string,
): { expectedAnswer: string; acceptedAnswers: string[] } | null {
  const match = compact.match(/(-?\d+)%(?:of|מתוך)?(-?\d+)/i);
  if (!match) return null;
  const p = Number(match[1]);
  const n = Number(match[2]);
  if (!Number.isInteger(p) || !Number.isInteger(n) || p < 0 || n < 0) {
    return null;
  }
  if ((p * n) % 100 !== 0) return null;
  const value = (p * n) / 100;
  const expected = String(value);
  if (compact.includes(`=${expected}`)) return null;
  if (!claimedIs(claimed, expected)) return null;
  return { expectedAnswer: expected, acceptedAnswers: [] };
}

function verifyArithmetic(
  compact: string,
  claimed: string,
): { expectedAnswer: string; acceptedAnswers: string[] } | null {
  if (/(-?\d+)[+\-*/](-?\d+)=-?\d+$/.test(compact)) return null;
  const match = compact.match(/(-?\d+)([+\-*/])(-?\d+)=?$/);
  if (!match) return null;
  const a = Number(match[1]);
  const op = match[2];
  const b = Number(match[3]);
  if (!Number.isInteger(a) || !Number.isInteger(b)) return null;
  let value: number;
  if (op === "+") value = a + b;
  else if (op === "-") value = a - b;
  else if (op === "*") value = a * b;
  else {
    if (b === 0 || a % b !== 0) return null;
    value = a / b;
  }
  if (!Number.isInteger(value)) return null;
  const expected = String(value);
  if (!claimedIs(claimed, expected)) return null;
  return { expectedAnswer: expected, acceptedAnswers: [] };
}

/**
 * Recompute the answer from the prompt. A claimed answer is kept only when
 * it matches that recomputation. Anything else is dropped.
 */
export function verifyModelExercise(
  prompt: string,
  claimedAnswer: string,
): {
  prompt: string;
  expectedAnswer: string;
  acceptedAnswers: string[];
} | null {
  const shown = prompt.replace(/\s+/g, " ").trim();
  if (!shown || !claimedAnswer.trim()) return null;
  const compact = compactPrompt(shown);
  const solved =
    verifyLinear(compact, claimedAnswer) ??
    verifyPercent(compact, claimedAnswer) ??
    verifyArithmetic(compact, claimedAnswer);
  if (!solved) return null;
  return { prompt: shown, ...solved };
}

export function verifiedArithmeticInstance(opts: {
  id: string;
  prompt: string;
  claimedAnswer: string;
  locale: PracticeLocale;
  now?: Date;
}): PracticeInstance | null {
  if (!isPracticeInstanceId(opts.id)) return null;
  const verified = verifyModelExercise(opts.prompt, opts.claimedAnswer);
  if (!verified) return null;
  const instance: PracticeInstance = {
    id: opts.id,
    generatorId: "verified.arithmetic",
    skillId: "verified.arithmetic",
    grade: 6,
    difficulty: "intro",
    seed: 0,
    locale: opts.locale,
    name: opts.locale === "he" ? "תרגיל מאומת" : "Verified drill",
    prompt: verified.prompt,
    hints: [],
    subject: "General",
    band: "elementary",
    expectedAnswer: verified.expectedAnswer,
    acceptedAnswers: verified.acceptedAnswers,
    createdAt: (opts.now ?? new Date()).toISOString(),
  };
  try {
    assertInstanceVerified(instance);
  } catch {
    return null;
  }
  return instance;
}

export function keepVerifiedModelExercises(
  exercises: Array<{ prompt: string; answer?: string }>,
  locale: PracticeLocale,
  makeId: () => string,
  now?: Date,
): Array<{ instance: PracticeInstance; exercise: VerifiedDrillExercise }> {
  const kept: Array<{ instance: PracticeInstance; exercise: VerifiedDrillExercise }> =
    [];
  for (const item of exercises) {
    if (kept.length >= 5) break;
    const id = makeId();
    const instance = verifiedArithmeticInstance({
      id,
      prompt: item.prompt,
      claimedAnswer: item.answer ?? "",
      locale,
      now,
    });
    if (!instance) continue;
    kept.push({ instance, exercise: toVerifiedExercise(instance) });
  }
  return kept;
}

export function clampGeneratorGrade(
  generatorId: string,
  grade: number,
): number | null {
  const meta = getPracticeGeneratorMeta(generatorId);
  if (!meta) return null;
  if (!Number.isInteger(grade)) return meta.gradeMin;
  if (grade < meta.gradeMin) return meta.gradeMin;
  if (grade > meta.gradeMax) return meta.gradeMax;
  return grade;
}

/** Fresh code-checked siblings. Prompts already shown are skipped. */
export function mintFreshDrills(opts: {
  generatorId: string;
  grade: number;
  locale: PracticeLocale;
  seed: number;
  count?: number;
  avoidPrompts?: string[];
  now?: Date;
  makeId: (index: number) => string;
}): PracticeInstance[] {
  const grade = clampGeneratorGrade(opts.generatorId, opts.grade);
  if (grade == null) return [];
  const count = Math.min(5, Math.max(1, opts.count ?? 3));
  const avoid = new Set((opts.avoidPrompts ?? []).map((prompt) => prompt.trim()));
  const out: PracticeInstance[] = [];
  const base = opts.seed >>> 0 || 1;
  for (let attempt = 0; attempt < 24 && out.length < count; attempt += 1) {
    const seed = (base + attempt * 0x9e3779b1) >>> 0 || 1;
    if (out.some((row) => row.seed === seed)) continue;
    try {
      const instance = mintPracticeInstance({
        id: opts.makeId(out.length),
        generatorId: opts.generatorId,
        grade,
        locale: opts.locale,
        seed,
        now: opts.now,
      });
      if (avoid.has(instance.prompt) || out.some((row) => row.prompt === instance.prompt)) {
        continue;
      }
      avoid.add(instance.prompt);
      out.push(instance);
    } catch {
      continue;
    }
  }
  return out;
}
